import React, { useEffect, useRef } from 'react';
import { Box, Grid, TextField, Button, styled, Typography } from '@mui/material';
import BaseCodeSelectbox from '../common/form/selectbox/BaseCodeSelectbox';
import TextMaskBusinessNo from '../common/form/TextMaskBusinessNo';

const FormLabel = styled(Box)(({ theme }) => ({
  //backgroundColor:'#ddd',
  ...theme.typography.subtitle2,
  borderRight: `1px solid ${theme.palette.divider}`,
  textAlign: 'center',
}));

// 필수 필드 표시용 별표 (업무 흐름 점검 2026-10-20 이슈 #1 — 기존에는 어떤 필드에도
// 별표가 안 붙어 사용자가 무엇이 필수인지 알 방법이 없었음).
const RequiredMark = () => (
  <Typography
    variant="subtitle2"
    component="span"
    sx={{ ml: 0.5, color: 'error.main' }}
  >
    *
  </Typography>
);

// 필드별로 "제출 시도했는지"까지 고려해 에러를 보여준다.
// 기존에는 formik.touched[field]만 봤기 때문에, 그 필드를 한 번도 건드리지
// 않은 채 저장을 누르면(= 포커스를 준 적이 없으면) 에러가 전혀 보이지 않고
// 저장만 조용히 막히는 문제가 있었다(이슈 #1). submitCount가 1 이상이면
// 터치 여부와 무관하게 에러를 보여준다.
const useFieldError = (formik) => {
  const hasError = (field) =>
    (formik.touched[field] || formik.submitCount > 0) &&
    Boolean(formik.errors[field]);
  const errorText = (field) =>
    (formik.touched[field] || formik.submitCount > 0) && formik.errors[field];
  return { hasError, errorText };
};

// 제출 시도 후에도 유효하지 않으면 가장 먼저 걸린 필드로 스크롤·포커스한다
// (이슈 #1 — 저장이 막혀도 화면이 전혀 바뀌지 않아 사용자가 원인을 알 수 없었음).
const FIELD_ORDER = [
  'partnerTyp',
  'incTyp',
  'regNumber',
  'partnerNm',
  'loanAmt',
  'partnerPhone',
  'partnerCd',
  'partnerAddr',
];

const PartnerForm = ({ formik, type }) => {
  const isAdd = type === 'add';
  const { hasError, errorText } = useFieldError(formik);

  const fieldRefs = {
    partnerTyp: useRef(null),
    incTyp: useRef(null),
    regNumber: useRef(null),
    partnerNm: useRef(null),
    loanAmt: useRef(null),
    partnerPhone: useRef(null),
    partnerCd: useRef(null),
    partnerAddr: useRef(null),
  };

  useEffect(() => {
    if (!formik.submitCount || formik.isValidating || formik.isValid) return;
    const firstErrorField = FIELD_ORDER.find((field) => formik.errors[field]);
    const node = firstErrorField && fieldRefs[firstErrorField]?.current;
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = node.querySelector?.('input, select, textarea');
      input?.focus?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount, formik.isValidating]);

  // Handler for checkbox changes that updates the tradeTyp field
  // PartnerForm 내부
  const handleTradeTypChange = (event) => {
    const { value, checked } = event.target; // value: '매출처' 등
    const current = Array.isArray(formik.values.tradeTyp)
      ? formik.values.tradeTyp
      : [];

    const next = checked
      ? [...current, value] // 체크 추가
      : current.filter((v) => v !== value); // 체크 해제하면 제거

    formik.setFieldValue('tradeTyp', next);
  };

  // Check if a specific trade type is selected
  const isTradeTypSelected = (type) => {
    const raw = formik.values.tradeTyp;
    // 1) 배열인 경우
    if (Array.isArray(raw)) {
      return raw.includes(type);
    }
    // 2) 혹시 문자열("매출처,제조사")로 남아있다면 split 후 trim
    if (typeof raw === 'string' && raw.length > 0) {
      return raw.split(',').map((s) => s.trim()).includes(type);
    }
    // 3) 그 외(빈값 등)
    return false;
  };

  return (
    <div>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: 1,
          px: 2,
        }}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid item xs={3}>
            <FormLabel>
              거래처 종류
              <RequiredMark />
            </FormLabel>
          </Grid>
          <Grid item xs={3} ref={fieldRefs.partnerTyp}>
            <BaseCodeSelectbox
              formik={formik}
              variant="outlined"
              required={true}
              fullWidth
              filterObject={{
                blcCd: 'BASE',
                mlcCd: 'PARTNER',
                slcCd: 'PARTNER_TYP',
                useYn: 'Y',
              }}
              name="partnerTyp"
              size="small"
            />
          </Grid>
          <Grid item xs={3}>
            <FormLabel>
              산업체 유형
              <RequiredMark />
            </FormLabel>
          </Grid>
          <Grid item xs={3} ref={fieldRefs.incTyp}>
            <BaseCodeSelectbox
              formik={formik}
              variant="outlined"
              required={true}
              fullWidth
              filterObject={{
                blcCd: 'BASE',
                mlcCd: 'FORCAST',
                slcCd: 'INC_TYP',
                useYn: 'Y',
              }}
              name="incTyp"
              size="small"
            />
          </Grid>
        </Grid>

        {/* New tradeTyp Field with Checkboxes */}
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* <Grid item xs={3}>
            <FormLabel>거래 유형</FormLabel>
          </Grid>
          <Grid item xs={3}>
          <FormGroup row>
  {['매출처','매입처','제조사'].map((label) => (
    <FormControlLabel
      key={label}
      control={
        <Checkbox
          size="small"
          value={label}                                    // 👉 name 대신 value 사용
          checked={formik.values.tradeTyp.includes(label)}// 배열.includes
          onChange={handleTradeTypChange}
        />
      }
      label={label}
    />
  ))}
</FormGroup>
{formik.touched.tradeTyp && formik.errors.tradeTyp && (
  <FormHelperText error>{formik.errors.tradeTyp}</FormHelperText>
)}
          </Grid> */}
          <Grid item xs={3}>
            <FormLabel>
              사업자번호
              <RequiredMark />
            </FormLabel>
          </Grid>
          <Grid item xs={3} ref={fieldRefs.regNumber}>
            <TextField
              size="small"
              variant="outlined"
              required
              fullWidth
              id="regNumber"
              name="regNumber"
              value={formik.values.regNumber}
              onChange={(event) => {
                // console.log(event.target.value);
                formik.setFieldValue('regNumber', event.target.value);
              }}
              error={hasError('regNumber')}
              helperText={errorText('regNumber')}
              InputProps={{
                inputComponent: TextMaskBusinessNo,
              }}
            />
          </Grid>
        </Grid>

        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid item xs={3}>
            <FormLabel>
              거래처명
              <RequiredMark />
            </FormLabel>
          </Grid>
          <Grid item xs={3} ref={fieldRefs.partnerNm}>
            <TextField
              size="small"
              variant="outlined"
              required
              fullWidth
              id="partnerNm"
              name="partnerNm"
              value={formik.values.partnerNm}
              onChange={formik.handleChange}
              error={hasError('partnerNm')}
              helperText={errorText('partnerNm')}
            />
          </Grid>
          <Grid item xs={3}>
            <FormLabel>
              여신한도
              <RequiredMark />
            </FormLabel>
          </Grid>
          <Grid item xs={3} ref={fieldRefs.loanAmt}>
            <TextField
              size="small"
              variant="outlined"
              required
              fullWidth
              id="loanAmt"
              name="loanAmt"
              value={formik.values.loanAmt}
              onChange={formik.handleChange}
              error={hasError('loanAmt')}
              helperText={errorText('loanAmt')}
            />
          </Grid>
        </Grid>

        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid item xs={3}>
            <FormLabel>
              연락처
              <RequiredMark />
            </FormLabel>
          </Grid>
          <Grid item xs={isAdd ? 9 : 3} ref={fieldRefs.partnerPhone}>
            <TextField
              size="small"
              variant="outlined"
              required
              fullWidth
              id="partnerPhone"
              name="partnerPhone"
              value={formik.values.partnerPhone}
              onChange={formik.handleChange}
              error={hasError('partnerPhone')}
              helperText={errorText('partnerPhone')}
            />
          </Grid>

          {/* 거래처코드는 신규 등록 시에는 사용자가 입력할 값이 아니다(서버가 관리) —
              KSM과 동일하게 등록 화면에서는 숨기고, 수정 화면에서만 읽기 전용으로
              보여준다(업무 흐름 점검 2026-10-20 이슈 #1 — 필수인데 입력 UI는 안내가
              없어 저장이 조용히 막히던 원인 중 하나). */}
          {!isAdd && (
            <>
              <Grid item xs={3}>
                <FormLabel>거래처코드</FormLabel>
              </Grid>
              <Grid item xs={3} ref={fieldRefs.partnerCd}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  disabled
                  id="partnerCd"
                  name="partnerCd"
                  value={formik.values.partnerCd}
                  onChange={formik.handleChange}
                  error={hasError('partnerCd')}
                  helperText={errorText('partnerCd')}
                />
              </Grid>
            </>
          )}
        </Grid>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid item xs={3}>
            <FormLabel>
              주소
              <RequiredMark />
            </FormLabel>
          </Grid>
          <Grid item xs={9} ref={fieldRefs.partnerAddr}>
            <TextField
              size="small"
              variant="outlined"
              required
              fullWidth
              id="partnerAddr"
              name="partnerAddr"
              value={formik.values.partnerAddr}
              onChange={formik.handleChange}
              error={hasError('partnerAddr')}
              helperText={errorText('partnerAddr')}
            />
          </Grid>
        </Grid>
        {/* 대표자명 / 은행명·계좌번호 (BITO_0425 p21, 0515 p21 "내역 추가", 2026-10-20) */}
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid item xs={3}>
            <FormLabel>대표자명</FormLabel>
          </Grid>
          <Grid item xs={3}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              id="president"
              name="president"
              value={formik.values.president}
              onChange={formik.handleChange}
              error={hasError('president')}
              helperText={errorText('president')}
            />
          </Grid>
          <Grid item xs={3}>
            <FormLabel>은행명</FormLabel>
          </Grid>
          <Grid item xs={3}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              id="bankName"
              name="bankName"
              value={formik.values.bankName}
              onChange={formik.handleChange}
              error={hasError('bankName')}
              helperText={errorText('bankName')}
            />
          </Grid>
        </Grid>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid item xs={3}>
            <FormLabel>계좌번호</FormLabel>
          </Grid>
          <Grid item xs={3}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              id="accountNumber"
              name="accountNumber"
              value={formik.values.accountNumber}
              onChange={formik.handleChange}
              error={hasError('accountNumber')}
              helperText={errorText('accountNumber')}
            />
          </Grid>
        </Grid>

        {/* 세금계산서 발행 메일/담당자/연락처 */}
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid item xs={3}>
            <FormLabel>세금계산서 발행 메일주소</FormLabel>
          </Grid>
          <Grid item xs={3}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              id="taxEmail"
              name="taxEmail"
              value={formik.values.taxEmail}
              onChange={formik.handleChange}
              error={hasError('taxEmail')}
              helperText={errorText('taxEmail')}
            />
          </Grid>
          <Grid item xs={3}>
            <FormLabel>세금계산서 담당자</FormLabel>
          </Grid>
          <Grid item xs={3}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              id="taxContactNm"
              name="taxContactNm"
              value={formik.values.taxContactNm}
              onChange={formik.handleChange}
              error={hasError('taxContactNm')}
              helperText={errorText('taxContactNm')}
            />
          </Grid>
        </Grid>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid item xs={3}>
            <FormLabel>세금계산서 연락처</FormLabel>
          </Grid>
          <Grid item xs={3}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              id="taxContactPhone"
              name="taxContactPhone"
              value={formik.values.taxContactPhone}
              onChange={formik.handleChange}
              error={hasError('taxContactPhone')}
              helperText={errorText('taxContactPhone')}
            />
          </Grid>
        </Grid>

        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid item xs={3}>
            <FormLabel>참고사항</FormLabel>
          </Grid>
          <Grid item xs={9}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              id="partnerDesc"
              name="partnerDesc"
              multiline
              rows={5}
              value={formik.values.partnerDesc}
              onChange={formik.handleChange}
              error={hasError('partnerDesc')}
              helperText={errorText('partnerDesc')}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 2,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            저장
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default PartnerForm;
